/**
 * Admin assignment API (Registry service)
 */

import apiClient from './auth';

export interface UnassignedSubmission {
  submission_uuid: string;
  title: string;
  abstract: string;
  current_state: string;
  technology_area?: string;
  keywords?: string[];
  submission_date: string;
  assignment_status?: string;
  submitter_uuid: string;
  submitter_email?: string;
  institution_name?: string | null;
}

export interface AvailableReviewer {
  reviewer_uuid: string;
  email: string;
  profile: {
    primary_expertise: string[];
    secondary_expertise?: string[];
    years_experience?: number;
    industry_background?: string | null;
    assessment_volume?: string | null;
    availability_status?: string | null;
    bio?: string | null;
  };
  capacity: {
    current_assignments: number;
    max_assignments: number;
    available_slots: number;
    utilization_percentage: number;
  };
}

export interface AssignmentRecord {
  assignment_uuid: string;
  submission: {
    submission_uuid: string;
    title: string;
    technology_area?: string;
  };
  reviewer: { reviewer_uuid: string; name: string };
  assigned_by: { admin_uuid: string; name: string };
  assigned_at: string;
  assignment_status: string;
  started_at?: string | null;
  completed_at?: string | null;
  priority?: string;
  due_date?: string | null;
  admin_notes?: string | null;
  days_remaining?: number | null;
  is_overdue?: boolean;
}

export interface AssignmentsListResponse {
  assignments: AssignmentRecord[];
  pagination: { total: number; page: number; limit: number; total_pages: number };
  summary: {
    total_assignments: number;
    pending: number;
    in_progress: number;
    completed: number;
    overdue: number;
  };
}

export async function getUnassignedSubmissions(params?: {
  status?: string;
  tech_area?: string;
  submission_date_from?: string;
  submission_date_to?: string;
  page?: number;
  limit?: number;
}): Promise<{
  submissions: UnassignedSubmission[];
  pagination: { total: number; page: number; limit: number; total_pages: number };
}> {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.tech_area) search.set('tech_area', params.tech_area);
  if (params?.submission_date_from) search.set('submission_date_from', params.submission_date_from);
  if (params?.submission_date_to) search.set('submission_date_to', params.submission_date_to);
  if (params?.page != null) search.set('page', String(params.page));
  if (params?.limit != null) search.set('limit', String(params.limit));
  const q = search.toString();
  const response = await apiClient.get<{
    submissions: UnassignedSubmission[];
    pagination: { total: number; page: number; limit: number; total_pages: number };
  }>(`/admin/submissions/unassigned${q ? `?${q}` : ''}`);
  return response.data;
}

export async function getAvailableReviewers(params?: {
  expertise?: string;
  availability_status?: string;
}): Promise<{ reviewers: AvailableReviewer[] }> {
  const search = new URLSearchParams();
  if (params?.expertise) search.set('expertise', params.expertise);
  if (params?.availability_status) search.set('availability_status', params.availability_status);
  const q = search.toString();
  const response = await apiClient.get<{ reviewers: AvailableReviewer[] }>(
    `/admin/reviewers/available${q ? `?${q}` : ''}`
  );
  return response.data;
}

export interface CreateAssignmentResponse {
  assignment_uuid: string;
  submission: { submission_uuid: string; title: string };
  reviewer: { reviewer_uuid: string; name: string };
  assigned_at: string;
  assignment_status: string;
  priority?: string;
  due_date?: string | null;
  notification_sent: boolean;
}

export async function createAssignment(body: {
  submission_uuid: string;
  reviewer_uuid: string;
  priority?: string;
  due_date?: string;
  admin_notes?: string;
}): Promise<CreateAssignmentResponse> {
  const response = await apiClient.post<CreateAssignmentResponse>('/admin/assignments/create', body);
  return response.data;
}

export async function listAssignments(params?: {
  status?: string;
  reviewer_uuid?: string;
  overdue?: boolean;
  page?: number;
  limit?: number;
}): Promise<AssignmentsListResponse> {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.reviewer_uuid) search.set('reviewer_uuid', params.reviewer_uuid);
  if (params?.overdue) search.set('overdue', 'true');
  if (params?.page != null) search.set('page', String(params.page));
  if (params?.limit != null) search.set('limit', String(params.limit));
  const q = search.toString();
  const response = await apiClient.get<AssignmentsListResponse>(`/admin/assignments${q ? `?${q}` : ''}`);
  return response.data;
}

export interface ReassignAssignmentResponse {
  message: string;
  old_assignment: { assignment_uuid: string; reviewer: string; status: string };
  new_assignment: { assignment_uuid: string; reviewer: string; reviewer_name?: string; assigned_at: string; status: string };
}

export async function reassignAssignment(
  assignmentUuid: string,
  body: { new_reviewer_uuid: string; reassignment_reason?: string }
): Promise<ReassignAssignmentResponse> {
  const response = await apiClient.post<ReassignAssignmentResponse>(
    `/admin/assignments/${assignmentUuid}/reassign`,
    body
  );
  return response.data;
}

export async function approveReviewerFeedback(submissionUuid: string): Promise<{ message: string; feedback_approval_status: string }> {
  const response = await apiClient.post<{ message: string; feedback_approval_status: string }>(
    `/admin/feedback/${submissionUuid}/approve`
  );
  return response.data;
}

export async function requestReviewerFeedbackChanges(
  submissionUuid: string,
  reason?: string
): Promise<{ message: string; feedback_approval_status: string }> {
  const response = await apiClient.post<{ message: string; feedback_approval_status: string }>(
    `/admin/feedback/${submissionUuid}/request-changes`,
    reason ? { reason } : undefined
  );
  return response.data;
}

export interface FilingRequestReportItem {
  submission_uuid: string;
  title: string;
  technology_area?: string;
  status: string;
  feedback_approval_status?: string;
  filing_request_status: 'none' | 'received' | 'in_support' | 'ready_to_file' | 'closed';
  filing_request_selected_types?: string[];
  filing_request_notified_at?: string;
  filing_request_received_at?: string;
  filing_request_in_support_at?: string;
  filing_request_ready_at?: string;
  filing_request_closed_at?: string;
  filing_transition_requested_status?: 'in_support' | 'ready_to_file' | 'closed';
  filing_transition_requested_at?: string;
  filing_transition_rejection_reason?: string;
  innovator_email?: string;
  reviewer_email?: string;
}

export async function getFilingRequestReport(params?: {
  status?: 'all' | 'received' | 'in_support' | 'ready_to_file' | 'closed';
  from?: string;
  to?: string;
}): Promise<{ summary: Array<{ filing_request_status: string; total: number }>; total: number; items: FilingRequestReportItem[] }> {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.from) search.set('from', params.from);
  if (params?.to) search.set('to', params.to);
  const q = search.toString();
  const response = await apiClient.get<{ summary: Array<{ filing_request_status: string; total: number }>; total: number; items: FilingRequestReportItem[] }>(
    `/admin/reports/filing-requests${q ? `?${q}` : ''}`
  );
  return response.data;
}

export async function approveFilingTransition(submissionUuid: string): Promise<{ message: string; filing_request_status: string }> {
  const response = await apiClient.post<{ message: string; filing_request_status: string }>(
    `/admin/filing-requests/${submissionUuid}/approve-transition`
  );
  return response.data;
}

export async function rejectFilingTransition(submissionUuid: string, reason?: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    `/admin/filing-requests/${submissionUuid}/reject-transition`,
    reason ? { reason } : undefined
  );
  return response.data;
}

/** Individual submissions only: super admin records billing so the innovator may request filing documentation prep. */
export async function recordFilingDocPrepBillingCleared(submissionUuid: string): Promise<{ message: string; submission_uuid: string }> {
  const response = await apiClient.post<{ message: string; submission_uuid: string }>(
    `/admin/submissions/${submissionUuid}/filing-documentation/record-billing-cleared`
  );
  return response.data;
}

export async function setFilingDocumentationPackage(
  submissionUuid: string,
  body: { package_url: string; mark_ready?: boolean }
): Promise<{ message: string; submission_uuid: string }> {
  const response = await apiClient.patch<{ message: string; submission_uuid: string }>(
    `/admin/submissions/${submissionUuid}/filing-documentation/package`,
    body
  );
  return response.data;
}
